import { AlertCircle, CheckCircle2, Loader2, Send } from 'lucide-react';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnswerRegion, BlockBoundingBox, ExamLayout } from '../../../db/api/exam-creation/exam-structure.js';
import type {
  StudentAnswerInput,
  StudentExamPayload,
  StudentMaterialAnswerInput,
} from '../../../db/api/student-exam.js';
import { getStudentExamQuery, submitStudentExamMutation } from '../../../db/api/student-exam.js';
import {
  answerKindForRegion,
  answerToPayload,
  BlankFieldInput,
  CalloutLabelInput,
  OverlayAnswer,
  parseChoice,
  parseMapping,
  withBlank,
  withLabel,
  type AnswerKind,
  type TaskAnswerState,
} from '../../components/student-exam/answer-widgets.js';
import { authIntentUrl } from '../../lib/auth-intent.js';
import { useCurrentUser } from '../../lib/useCurrentUser.js';

interface ExamTaskNode {
  _id?: string;
  taskNumber?: string;
  description?: string;
  taskType?: string;
  subTasks?: ExamTaskNode[];
  /** Materials (figures/tables/etc.) this task references — see `slots` below and `Exam.tasks`. */
  additionalInfoIds?: string[];
  /** Figures embedded directly in this task's own body, no `#material` wrapper — see `materialSlots`,
   * which treats these exactly like `additionalInfoIds`: both are just ids into `layout.boxes`. */
  inlineFigureIds?: string[];
}

interface AnswerSlot {
  path: string;
  task: ExamTaskNode;
  region: AnswerRegion;
  kind: AnswerKind;
  /**
   * `kind === 'drawing'` only — this task's own printed PROMPT area (not its answer region) AND, for a
   * subtask, every ancestor's own prompt above it in printed order — e.g. task "5"'s own intro text, THEN
   * "5.a"'s own prompt, for the drawing surface on "5.a"'s answer. Shown above the zoomed editor instead of
   * a re-typed copy of `task.description`: the printed prompt can carry formatting (formulas, emphasis,
   * wrapped figures) a plain string can't, and showing the actual page content can never drift from what's
   * really printed. See `slots` below for how these are carved out of each ancestor's own whole box.
   */
  promptBoxes?: BlockBoundingBox[];
}

const UNFRAMED_KINDS = new Set<AnswerKind>(['choice', 'mapping', 'promptList']);
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;

function boxStyle(box: BlockBoundingBox, pageWidthPt: number, pageHeightPt: number, pageCount: number) {
  const totalHeightPt = pageHeightPt * pageCount;
  const pageIndex = Math.max(0, (box.page || 1) - 1);
  return {
    left: `${(box.x / pageWidthPt) * 100}%`,
    top: `${((pageIndex * pageHeightPt + box.y) / totalHeightPt) * 100}%`,
    width: `${(box.width / pageWidthPt) * 100}%`,
    height: `${(box.height / totalHeightPt) * 100}%`,
  };
}

/**
 * A `#gap`/`#prompt-list` blank's printed box is only as tall as the printed rule needs (0.9em, see
 * exam.typ) — exactly right for reproducing the page, much too tight for an on-screen field: a click
 * anywhere near the line should focus it, and a typed descender (g, y, j, ...) needs room below the rule
 * it would not need on the page, where nothing is ever actually written until the PDF is regenerated.
 * Padding the on-screen box alone, above and below, leaves the print geometry these numbers come from
 * untouched — only the interactive overlay grows.
 */
const BLANK_FIELD_PAD_ABOVE_PT = 5;
const BLANK_FIELD_PAD_BELOW_PT = 4;

function blankFieldStyle(box: BlockBoundingBox, pageWidthPt: number, pageHeightPt: number, pageCount: number) {
  const totalHeightPt = pageHeightPt * pageCount;
  const pageIndex = Math.max(0, (box.page || 1) - 1);
  const topPt = pageIndex * pageHeightPt + box.y - BLANK_FIELD_PAD_ABOVE_PT;
  const heightPt = box.height + BLANK_FIELD_PAD_ABOVE_PT + BLANK_FIELD_PAD_BELOW_PT;
  return {
    left: `${(box.x / pageWidthPt) * 100}%`,
    top: `${(topPt / totalHeightPt) * 100}%`,
    width: `${(box.width / pageWidthPt) * 100}%`,
    height: `${(heightPt / totalHeightPt) * 100}%`,
  };
}

/**
 * The answer box as a fraction of its OWN page — the space the drawing editor's printed backdrop works in.
 * Unlike `boxStyle` above, this is single-page-relative, not stacked-document-relative: the backdrop is one
 * page image (`pageImageUrls[box.page - 1]`, see `StudentExamPayload`), not the whole exam concatenated.
 */
function cropOf(box: BlockBoundingBox, pageWidthPt: number, pageHeightPt: number) {
  return {
    left: box.x / pageWidthPt,
    top: box.y / pageHeightPt,
    width: box.width / pageWidthPt,
    height: box.height / pageHeightPt,
  };
}

function walkTasks(tasks: ExamTaskNode[], parentPath: string, visit: (task: ExamTaskNode, path: string) => void) {
  for (const task of tasks) {
    const path = parentPath ? `${parentPath}.${task.taskNumber ?? ''}` : (task.taskNumber ?? '');
    visit(task, path);
    if (Array.isArray(task.subTasks)) walkTasks(task.subTasks, path, visit);
  }
}

/** Memoized individual slot overlay to isolate re-renders when a single answer updates */
const AnswerSlotOverlay = React.memo(function AnswerSlotOverlay({
  slot,
  answer,
  onChange,
  pageWidthPt,
  pageHeightPt,
  pageCount,
  pageImageUrls,
}: {
  slot: AnswerSlot;
  answer: TaskAnswerState | undefined;
  onChange: (next: TaskAnswerState) => void;
  pageWidthPt: number;
  pageHeightPt: number;
  pageCount: number;
  /** One PNG per printed page — see `StudentExamPayload.pageImageUrls`. Only the drawing kind's backdrop
   * reads this; everything else on the page positions against `pageWidthPt`/`pageHeightPt`/`pageCount`
   * alone. */
  pageImageUrls: string[];
}) {
  const calloutBoxes = slot.kind === 'labels' ? slot.region.calloutBoxes : undefined;

  if (calloutBoxes && calloutBoxes.length > 0) {
    return (
      <Fragment>
        {calloutBoxes.map((calloutBox, index) => (
          <div
            key={index}
            style={boxStyle(calloutBox, pageWidthPt, pageHeightPt, pageCount)}
            className="absolute overflow-hidden rounded-[1.5px] transition-shadow focus-within:ring-2 focus-within:ring-brand-500/60"
          >
            <CalloutLabelInput
              value={answer?.labels?.[index] ?? ''}
              onChange={next => onChange(withLabel(answer, index, next, calloutBoxes.length))}
            />
          </div>
        ))}
      </Fragment>
    );
  }

  // GAP_TEXT: a `#gap` per blank scattered through the task's own flowing text, each its own small point
  // mark with no single widget box to route through `OverlayAnswer` — same reasoning as `calloutBoxes`
  // right above, and the same page-level early return.
  const gapBoxes = slot.kind === 'gapFill' ? slot.region.gapBoxes : undefined;

  if (gapBoxes && gapBoxes.length > 0) {
    return (
      <Fragment>
        {gapBoxes.map((gapBox, index) => (
          <div key={index} style={blankFieldStyle(gapBox, pageWidthPt, pageHeightPt, pageCount)} className="absolute overflow-hidden">
            <BlankFieldInput
              value={answer?.blanks?.[index] ?? ''}
              onChange={next => onChange(withBlank(answer, index, next, gapBoxes.length))}
            />
          </div>
        ))}
      </Fragment>
    );
  }

  const choice = slot.kind === 'choice' ? parseChoice(slot.task.description ?? '') : null;
  const mapping = slot.kind === 'mapping' ? parseMapping(slot.task.description ?? '') : null;
  const box = slot.region.box!;

  const rowRects = slot.region.optionRows?.map(row => ({
    topPercent: ((row.y - box.y) / box.height) * 100,
    heightPercent: (row.height / box.height) * 100,
  }));

  // The checkbox mark's own WIDTH, as a percentage of THIS box's width -- not a fixed CSS length.
  // `MC_CHECKBOX_PT` must track `#mc`'s own `width: 9pt, height: 9pt` in exam.typ (that macro's own printed
  // glyph, an outline the online mark sits over) -- a plain `9pt` CSS value on the mark itself doesn't:
  // `pt` is an absolute CSS unit, so it stays a fixed physical size on screen no matter how large or small
  // the exam page itself is being rendered, while everything about the exam's own layout (this box
  // included) scales with the viewport. A percentage of the box, like `rowRects` above, scales the same
  // way the box itself does.
  //
  // WIDTH ONLY, not height: the mark sits inside its own OPTION ROW, whose height (`rowRects[i]
  // .heightPercent`) is the row's own printed height, taller for a wrapped multi-line option — the
  // checkbox itself never grows with a wrapped option, on paper or online. Deriving height from a
  // percentage of the ROW's height would shrink or grow the mark with whatever that row's height happens
  // to be; deriving it from `aspect-ratio: 1` against this WIDTH instead keeps it square and correctly
  // sized off the one dimension (the row spans the box's full width) that isn't row-height-dependent.
  const MC_CHECKBOX_PT = 9;
  const markWidthPercent = (MC_CHECKBOX_PT / box.width) * 100;

  const LANE_INSET_PT = 11;
  const connectors = slot.region.matchRows?.map(row => ({
    topPercent: ((row.y - box.y) / box.height) * 100,
    leftPercent: ((row.x + LANE_INSET_PT - box.x) / box.width) * 100,
    rightPercent: ((row.rightX - LANE_INSET_PT - box.x) / box.width) * 100,
    hasLeft: row.hasLeft,
    hasRight: row.hasRight,
  }));

  // TRANSLATION: each `#prompt-list` row's own blank cell, as a percentage of the widget's whole box —
  // same reasoning as `rowRects`/`connectors` above, padded top and bottom the same way `blankFieldStyle`
  // pads a `#gap`'s box: the printed rect alone is too tight to click or to hold a typed descender.
  const promptRects = slot.region.promptRows?.map(row => ({
    topPercent: ((row.y - box.y - BLANK_FIELD_PAD_ABOVE_PT) / box.height) * 100,
    leftPercent: ((row.x - box.x) / box.width) * 100,
    widthPercent: (row.width / box.width) * 100,
    heightPercent: ((row.height + BLANK_FIELD_PAD_ABOVE_PT + BLANK_FIELD_PAD_BELOW_PT) / box.height) * 100,
  }));

  // Stable across a re-render triggered only by this slot's `answer` changing (a drawing's own emitted data
  // URL, most often) — `DrawingCanvas` is `memo`-wrapped specifically so that round trip doesn't force it to
  // re-render, which only works if `crop`'s object identity doesn't change too on every such update.
  const crop = useMemo(() => cropOf(box, pageWidthPt, pageHeightPt), [box, pageWidthPt, pageHeightPt]);
  const pageImageUrl = pageImageUrls[Math.max(0, (box.page || 1) - 1)];

  // `drawing` only — see `AnswerSlot.promptBoxes`. Both the crop fractions AND the real aspect ratio are
  // needed per box: `cropOf`'s fractions are each relative to a DIFFERENT page dimension (width to
  // `pageWidthPt`, height to `pageHeightPt`), so their ratio to each other isn't the box's actual on-page
  // aspect ratio unless the page happens to be square — `aspect` is that real ratio, in points, for sizing
  // each picture correctly before anything has loaded. `pageImageUrl` is resolved PER BOX, not shared with
  // the one above: an ancestor can be on an earlier page than the leaf's own answer (see `slots`'s own
  // comment on why that page-crossing is allowed here).
  const promptBoxes = slot.promptBoxes;
  const promptCrops = useMemo(
    () =>
      promptBoxes?.map(promptBox => ({
        crop: cropOf(promptBox, pageWidthPt, pageHeightPt),
        aspect: promptBox.width / promptBox.height,
        pageImageUrl: pageImageUrls[Math.max(0, (promptBox.page || 1) - 1)] ?? '',
      })),
    [promptBoxes, pageWidthPt, pageHeightPt, pageImageUrls],
  );

  const OPAQUE_PAD_PT = 1.5;
  const isOpaque = slot.kind === 'text';
  const containerBox = isOpaque
    ? {
        page: box.page,
        x: box.x - OPAQUE_PAD_PT,
        y: box.y - OPAQUE_PAD_PT,
        width: box.width + 2 * OPAQUE_PAD_PT,
        height: box.height + 2 * OPAQUE_PAD_PT,
      }
    : box;

  return (
    <div
      style={boxStyle(containerBox, pageWidthPt, pageHeightPt, pageCount)}
      className={
        UNFRAMED_KINDS.has(slot.kind)
          ? 'absolute'
          : 'absolute overflow-hidden rounded-sm ring-1 ring-inset ring-brand-500/20 transition-shadow focus-within:ring-2 focus-within:ring-brand-500/60'
      }
    >
      <OverlayAnswer
        kind={slot.kind}
        value={answer}
        onChange={onChange}
        lines={slot.region.lines}
        options={choice?.options}
        left={mapping?.left}
        right={mapping?.right}
        rowRects={rowRects}
        markWidthPercent={markWidthPercent}
        connectors={connectors}
        promptRects={promptRects}
        pageImageUrl={pageImageUrl}
        crop={crop}
        promptCrops={promptCrops}
      />
    </div>
  );
});

/**
 * One material's own drawable surface — see `materialSlots` in `StudentExamPage`. Always a `drawing`
 * `OverlayAnswer`: a material is a picture, never a text/choice/mapping field, so there is only ever the
 * one kind to render here.
 *
 * NO PROMPT SHOWN ABOVE THE ZOOMED EDITOR, unlike a task's own drawing region (`AnswerSlotOverlay`'s
 * `promptCrop`) — there IS no single prompt to show: a material can be linked to several tasks at once
 * (`materialSlot.taskPaths`), each with its own question, and a material embedded in exactly one task's
 * body is already shown WITHIN that task's own printed prompt on the page — the zoomed backdrop here is
 * already a picture of the thing being annotated, not a stand-in for it.
 */
const MaterialSlotOverlay = React.memo(function MaterialSlotOverlay({
  materialSlot,
  answer,
  onChange,
  pageWidthPt,
  pageHeightPt,
  pageCount,
  pageImageUrls,
}: {
  materialSlot: { materialId: string; box: BlockBoundingBox; taskPaths: string[] };
  answer: TaskAnswerState | undefined;
  onChange: (next: TaskAnswerState) => void;
  pageWidthPt: number;
  pageHeightPt: number;
  pageCount: number;
  pageImageUrls: string[];
}) {
  const box = materialSlot.box;
  const crop = useMemo(() => cropOf(box, pageWidthPt, pageHeightPt), [box, pageWidthPt, pageHeightPt]);
  const pageImageUrl = pageImageUrls[Math.max(0, (box.page || 1) - 1)];

  return (
    // No ring/border here on purpose, unlike `AnswerSlotOverlay` — a material/inline figure already sits
    // printed on the page with its own visible outline (a photo, a plot's own axes); framing it again would
    // draw a highlight box around content that was never meant to look like a form field.
    <div style={boxStyle(box, pageWidthPt, pageHeightPt, pageCount)} className="absolute overflow-hidden">
      <OverlayAnswer
        kind="drawing"
        value={answer}
        onChange={onChange}
        pageImageUrl={pageImageUrl}
        crop={crop}
      />
    </div>
  );
});

export default function StudentExamPage({ examId }: { examId: string }) {
  const { t } = useTranslation();
  const { user, isLoading: userLoading } = useCurrentUser();
  const [payload, setPayload] = useState<StudentExamPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<string, TaskAnswerState>>({});
  /** One entry per material with a drawing on it — keyed by material id, not task path (see `materialSlots`
   * and `handleSubmit`, which is what turns this into one or more `StudentMaterialAnswerInput`s). */
  const [materialAnswers, setMaterialAnswers] = useState<Record<string, TaskAnswerState>>({});
  const [studentName, setStudentName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);

  const draftKey = `gradias-student-exam-draft:${examId}`;

  useEffect(() => {
    if (userLoading) return;
    let cancelled = false;
    setLoading(true);

    void getStudentExamQuery(examId)()
      .then((raw: string) => {
        if (!cancelled) setPayload(JSON.parse(raw) as StudentExamPayload);
      })
      .catch(() => {
        if (!cancelled) setPayload({ ok: false, message: 'StudentExam.errors.loadFailed' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [examId, userLoading]);

  /**
   * ONE localStorage ENTRY PER SLOT, not one blob for the whole exam. The earlier version kept every
   * answer in a single key and re-`JSON.stringify`'d the whole thing (every drawing's base64 PNG included)
   * a second after EVERY keystroke in ANY field — typing a sentence into one text box was, a moment after
   * every pause, re-serializing and re-writing megabytes of unrelated drawings that hadn't changed. Typing
   * now only ever touches the ONE slot's own (typically tiny) entry.
   *
   * The pending write and its `setTimeout` are kept together so a component unmount (navigating away
   * mid-debounce) can still flush the actual value instead of just discarding it — the old effect-based
   * debounce couldn't do this because cleanup only had the timer, not the value it was about to write.
   */
  const pendingDraftsRef = useRef(new Map<string, { value: TaskAnswerState; timer: ReturnType<typeof setTimeout> }>());

  const scheduleDraftWrite = useCallback(
    (path: string, value: TaskAnswerState) => {
      const pending = pendingDraftsRef.current;
      const existing = pending.get(path);
      if (existing) clearTimeout(existing.timer);
      const timer = setTimeout(() => {
        pending.delete(path);
        try {
          window.localStorage.setItem(`${draftKey}:${path}`, JSON.stringify(value));
        } catch {
          // Quota exceeded guard
        }
      }, 600);
      pending.set(path, { value, timer });
    },
    [draftKey],
  );

  // Every key under this exam's draft prefix, PLUS the exact legacy blob key (a draft saved before this
  // per-slot rewrite) — kept as one helper since both loading and clearing need the same enumeration.
  const draftKeysAndLegacy = useCallback(() => {
    const prefix = `${draftKey}:`;
    const keys: string[] = [];
    let legacy: string | null = null;
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      if (key === draftKey) legacy = key;
      else if (key.startsWith(prefix)) keys.push(key);
    }
    return { prefix, keys, legacy };
  }, [draftKey]);

  useEffect(() => {
    try {
      const { prefix, keys, legacy } = draftKeysAndLegacy();
      const restored: Record<string, TaskAnswerState> = {};

      // Migrate an older, single-blob draft into today's per-slot keys — see `scheduleDraftWrite` above —
      // so a slot the student doesn't touch again this session isn't silently dropped once the blob is
      // removed below.
      if (legacy) {
        try {
          Object.assign(restored, JSON.parse(window.localStorage.getItem(legacy) ?? '{}'));
        } catch {
          // Ignore a corrupt legacy draft
        }
      }
      for (const key of keys) {
        try {
          restored[key.slice(prefix.length)] = JSON.parse(window.localStorage.getItem(key) ?? 'null');
        } catch {
          // Ignore one corrupt slot rather than losing every other restored answer
        }
      }

      if (Object.keys(restored).length > 0) setAnswers(restored);
      if (legacy) {
        for (const [path, value] of Object.entries(restored)) {
          try {
            window.localStorage.setItem(`${draftKey}:${path}`, JSON.stringify(value));
          } catch {
            // Quota exceeded guard
          }
        }
        window.localStorage.removeItem(legacy);
      }
    } catch {
      // Ignore storage access errors (privacy mode, disabled storage, ...)
    }
    // Runs once per exam: `draftKeysAndLegacy` only changes identity when `draftKey` does.
  }, [draftKeysAndLegacy, draftKey]);

  // Flushes any write still waiting out its debounce, so navigating away within the last 600ms of typing
  // doesn't lose it — the old whole-exam debounce had the same gap, but a single flush here is cheap enough
  // to just close for good.
  useEffect(() => {
    const pending = pendingDraftsRef.current;
    return () => {
      for (const [path, { value, timer }] of pending) {
        clearTimeout(timer);
        try {
          window.localStorage.setItem(`${draftKey}:${path}`, JSON.stringify(value));
        } catch {
          // Quota exceeded guard
        }
      }
      pending.clear();
    };
  }, [draftKey]);

  // A DIFFERENT prefix than task drafts (`-materials:` rather than task drafts' own `:`), not a sibling key
  // under the same one — `draftKeysAndLegacy` above sweeps up EVERY key starting with `${draftKey}:` into
  // the TASK-PATH-keyed `answers` map, and a material id living under that same prefix would land there
  // too, under a "path" no slot has. Keeping materials under their own prefix is what lets this restore
  // independently into `materialAnswers` instead.
  const materialDraftPrefix = `${draftKey}-materials:`;
  const pendingMaterialDraftsRef = useRef(
    new Map<string, { value: TaskAnswerState; timer: ReturnType<typeof setTimeout> }>(),
  );

  const scheduleMaterialDraftWrite = useCallback(
    (materialId: string, value: TaskAnswerState) => {
      const pending = pendingMaterialDraftsRef.current;
      const existing = pending.get(materialId);
      if (existing) clearTimeout(existing.timer);
      const timer = setTimeout(() => {
        pending.delete(materialId);
        try {
          window.localStorage.setItem(`${materialDraftPrefix}${materialId}`, JSON.stringify(value));
        } catch {
          // Quota exceeded guard
        }
      }, 600);
      pending.set(materialId, { value, timer });
    },
    [materialDraftPrefix],
  );

  useEffect(() => {
    try {
      const restored: Record<string, TaskAnswerState> = {};
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (!key || !key.startsWith(materialDraftPrefix)) continue;
        try {
          restored[key.slice(materialDraftPrefix.length)] = JSON.parse(window.localStorage.getItem(key) ?? 'null');
        } catch {
          // Ignore one corrupt material draft rather than losing every other restored one
        }
      }
      if (Object.keys(restored).length > 0) setMaterialAnswers(restored);
    } catch {
      // Ignore storage access errors (privacy mode, disabled storage, ...)
    }
  }, [materialDraftPrefix]);

  useEffect(() => {
    const pending = pendingMaterialDraftsRef.current;
    return () => {
      for (const [materialId, { value, timer }] of pending) {
        clearTimeout(timer);
        try {
          window.localStorage.setItem(`${materialDraftPrefix}${materialId}`, JSON.stringify(value));
        } catch {
          // Quota exceeded guard
        }
      }
      pending.clear();
    };
  }, [materialDraftPrefix]);

  const setMaterialAnswer = useCallback(
    (materialId: string, next: TaskAnswerState) => {
      setMaterialAnswers(previous => ({ ...previous, [materialId]: next }));
      scheduleMaterialDraftWrite(materialId, next);
    },
    [scheduleMaterialDraftWrite],
  );

  const clearDraft = useCallback(() => {
    for (const { timer } of pendingDraftsRef.current.values()) clearTimeout(timer);
    pendingDraftsRef.current.clear();
    for (const { timer } of pendingMaterialDraftsRef.current.values()) clearTimeout(timer);
    pendingMaterialDraftsRef.current.clear();
    try {
      const { keys, legacy } = draftKeysAndLegacy();
      for (const key of keys) window.localStorage.removeItem(key);
      if (legacy) window.localStorage.removeItem(legacy);
      for (let i = window.localStorage.length - 1; i >= 0; i--) {
        const key = window.localStorage.key(i);
        if (key?.startsWith(materialDraftPrefix)) window.localStorage.removeItem(key);
      }
    } catch {
      // ignore
    }
  }, [draftKeysAndLegacy, materialDraftPrefix]);

  const layout: ExamLayout | undefined = payload?.layout;
  const tasks = useMemo(() => (payload?.tasks ?? []) as ExamTaskNode[], [payload]);

  const pageWidthPt = layout?.pageWidthPt || A4_WIDTH_PT;
  const pageHeightPt = layout?.pageHeightPt || A4_HEIGHT_PT;
  // `payload.pageImageUrls` is one entry per ACTUAL printed page (straight off `Exam.blankExamIds`), so its
  // length is the real page count. The fallback below — the highest page number any TRACKED answer box sits
  // on — undercounts whenever a page carries no answerable task (an intro page, a page of pure prose between
  // tasks, a closing page): `boxStyle` divides every box's stacked position by `pageHeightPt * pageCount`, so
  // an undercounted `pageCount` is exact for page 1 (the error term is `pageIndex * 0`) and increasingly
  // wrong for every page after — the drawing widget's own zoomed crop doesn't drift the same way (`cropOf`
  // is purely page-local), but the on-screen box it's zoomed FROM would be stretched to the wrong size and
  // aspect ratio, distorting the enlarged view built from it. `pageImageUrls` never has this gap: it comes
  // from `pdfFile.getNumPages()`, not from which pages happen to have a tracked box on them.
  const pageCount = useMemo(() => {
    if (payload?.pageImageUrls?.length) return payload.pageImageUrls.length;
    const boxes = Object.values(layout?.boxes ?? {});
    return Math.max(1, ...boxes.map(box => box.page || 1));
  }, [payload, layout]);

  const slots = useMemo(() => {
    const regions = layout?.answers ?? {};
    const boxes = layout?.boxes ?? {};
    const found: AnswerSlot[] = [];
    walkTasks(tasks, '', (task, path) => {
      const region = regions[path];
      if (!region) return;
      const kind = region.calloutBoxes?.length
        ? 'labels'
        : region.gapBoxes?.length
          ? 'gapFill'
          : answerKindForRegion(region.answerType, task.taskType);
      if (kind === 'none') return;

      // `boxes[path]` is the task's WHOLE box (prompt + answer together — see the alias written in
      // `computeBoundingBoxes`); the answer's own box is where its own printed field starts. The slice
      // between the task box's top and the answer box's top is the prompt, and nothing else: on the same
      // page (guaranteed — a task and its answer render as one atomic, unbreakable block) and strictly
      // taller than zero (a task can decide there's nothing worth showing there, e.g. no answer box at all).
      //
      // FOR A SUBTASK, every ANCESTOR's own whole box comes first — "5"'s own intro text, then "5.a"'s own
      // prompt, for the drawing surface on "5.a"'s answer — because on paper the parent's own text is the
      // question a subtask's "a)"/"b)" continues, not something a student answering "5.a" already has in
      // view once zoomed in on just its own line. Each ancestor is used AS TRACKED, untrimmed: every task,
      // parent or leaf, prints as one `width: 100%` block anchored at the same left edge (indentation is an
      // internal padding, not a narrower tracked box), so no extra alignment work is needed for those.
      //
      // AN ANCESTOR CAN BE ON A DIFFERENT PAGE than the leaf's own answer — a sibling subtask's answer
      // growing (a long typed answer's ruled lines, see `answer-lines`) can push a LATER subtask onto the
      // next page while the shared parent's intro text stays on the page before it, so this does NOT
      // require `ancestorBox.page === region.box.page` the way the leaf's own trimmed box below does — each
      // box in the chain carries its own `page`, resolved to its own page image independently downstream
      // (`AnswerSlotOverlay`'s `promptCrops`). Skipping a cross-page ancestor here was the earlier bug: "5.a"
      // (same page as "5") showed both, "5.b" (pushed to the next page) silently showed only itself.
      //
      // ONLY THE LEAF'S OWN BOX is trimmed to its prompt (task top → answer top) and re-anchored to the
      // ANSWER's `x`/`width` rather than its own — that is what GUARANTEES the last picture in the stack
      // lines up exactly with the drawing crop directly below it (see `DrawingCanvas`'s own `taskPrompt`),
      // reading as one continuous crop rather than independently-cropped pictures that happen to be close.
      // Trimming this way NEEDS same-page, since it subtracts one box's `y` from another's.
      //
      // `PROMPT_TOP_PAD_PT` opens a little breathing room above the very first picture in the stack, clamped
      // at the page's own top edge. `PROMPT_BOTTOM_PAD_PT` extends every box EXCEPT the last one a little
      // past typst's own measured bottom edge — that measurement is tight to the text's ink, and a last
      // line ending in a descender (a "p", "g", "j") was getting its tail clipped by the crop otherwise.
      // Nothing is added at the bottom of the LAST box — that edge already IS the seam the drawing crop
      // continues from, and its own text never sits right at that edge the way an ancestor's can.
      const PROMPT_TOP_PAD_PT = 6;
      const PROMPT_BOTTOM_PAD_PT = 4;
      let promptBoxes: BlockBoundingBox[] | undefined;
      if (kind === 'drawing' && region.box) {
        const segments = path.split('.');
        const chain: BlockBoundingBox[] = [];
        for (let i = 1; i < segments.length; i++) {
          const ancestorBox = boxes[segments.slice(0, i).join('.')];
          if (ancestorBox) chain.push({ ...ancestorBox, height: ancestorBox.height + PROMPT_BOTTOM_PAD_PT });
        }
        const taskBox = boxes[path];
        if (taskBox && taskBox.page === region.box.page && region.box.y > taskBox.y) {
          // A material/inline figure this task embeds (already drawable on its own, see `materialSlots`)
          // prints somewhere between the task's own top and its answer — cropping straight through to
          // `region.box.y` would pull that figure into the "task description" picture too, which is not
          // what this stack is for (that's the whole page, on the page, already visible; this stack is
          // only ever meant to be the PATH THROUGH THE TASK TREE's own text). Stop the crop at the topmost
          // such figure instead, on the same page, above the answer — whatever printed after it in this
          // span is the answer region itself, already excluded.
          const embeddedIds = [...(task.additionalInfoIds ?? []), ...(task.inlineFigureIds ?? [])];
          const embeddedTops: number[] = [];
          for (const id of embeddedIds) {
            const b = boxes[id];
            if (b && b.page === taskBox.page && b.y > taskBox.y && b.y < region.box.y) embeddedTops.push(b.y);
          }
          const bottom = embeddedTops.length > 0 ? Math.min(region.box.y, ...embeddedTops) : region.box.y;
          if (bottom > taskBox.y) {
            chain.push({ page: taskBox.page, x: region.box.x, y: taskBox.y, width: region.box.width, height: bottom - taskBox.y });
          }
        }
        if (chain.length > 0) {
          const first = chain[0]!;
          const paddedTop = Math.max(0, first.y - PROMPT_TOP_PAD_PT);
          chain[0] = { ...first, y: paddedTop, height: first.y - paddedTop + first.height };
          promptBoxes = chain;
        }
      }

      found.push({ path, task, region, kind, promptBoxes });
    });
    return found;
  }, [tasks, layout]);

  const positioned = useMemo(
    () => slots.filter(slot => slot.region.box ?? slot.region.calloutBoxes?.length ?? slot.region.gapBoxes?.length),
    [slots],
  );

  /**
   * EVERY MATERIAL a task references gets its own drawable surface, regardless of whether that task also
   * has its own printed answer region — a teacher's task can reasonably expect the answer to be annotating
   * the figure itself ("label this diagram", "mark the vectors") rather than a separate field, and there is
   * no way to know which from the answer TYPE alone. `layout.boxes` tracks a material's own printed extent
   * keyed by its id, the same way it tracks every task/widget box (see `computeBoundingBoxes`).
   *
   * `additionalInfoIds` (a shared `#material(...)`) and `inlineFigureIds` (a figure sitting directly in the
   * task's own body, no wrapper) are treated identically here — both are just ids into the same `boxes`
   * map, and a drawable surface over one is exactly as valid as over the other.
   *
   * Grouped by material id, not one slot per (task, material) pair: a material can be linked to more than
   * one task (`#material(tasks: (...))` in exam.typ), and a shared figure gets ONE drawing surface, not a
   * duplicate copy per task that happens to reference it. `taskPaths` is who the drawing counts as an
   * answer FOR once submitted — see `handleSubmit` — every one of them if there's more than one, or just
   * that one task if there's only one, straight off the connection recorded here.
   */
  const materialSlots = useMemo(() => {
    const boxes = layout?.boxes ?? {};
    const regions = layout?.answers ?? {};
    const byMaterial = new Map<string, string[]>();
    walkTasks(tasks, '', (task, path) => {
      // A LABELLING task's own diagram is not drawable: its answer already IS the diagram's numbered
      // callout boxes (`region.calloutBoxes`, rendered by `AnswerSlotOverlay` as `kind: 'labels'`), and a
      // material's drawable surface sits in the SAME DOM stacking position (see the render order below —
      // material overlays paint after, i.e. on top of, answer overlays) and covers the diagram's WHOLE
      // printed box, callout gutters included (`layout.boxes[materialId]` is the full figure-block, not
      // just the image — see exam-structure.ts's `computeBoundingBoxes`). Left drawable, it silently
      // intercepts every click meant for those callout text inputs underneath it.
      if (regions[path]?.calloutBoxes?.length) return;
      const ids = [...(task.additionalInfoIds ?? []), ...(task.inlineFigureIds ?? [])];
      for (const materialId of ids) {
        if (!boxes[materialId]) continue;
        const taskPaths = byMaterial.get(materialId) ?? [];
        taskPaths.push(path);
        byMaterial.set(materialId, taskPaths);
      }
    });
    return [...byMaterial.entries()].map(([materialId, taskPaths]) => ({
      materialId,
      box: boxes[materialId]!,
      taskPaths,
    }));
  }, [tasks, layout]);

  // Parsing a slot's description (regex-based, for `choice`/`mapping`) only ever depends on `slots` — not on
  // `answers` — but `answeredCount` below needs to run it for every slot on every keystroke/stroke, since it
  // reruns whenever ANY answer changes. Precomputing it here means that repeated re-render only does cheap
  // map lookups instead of re-parsing every choice/mapping task's description from scratch each time.
  const parsedBySlot = useMemo(() => {
    const map = new Map<string, { options?: string[]; left?: string[] }>();
    for (const slot of slots) {
      map.set(
        slot.path,
        slot.kind === 'choice'
          ? { options: parseChoice(slot.task.description ?? '').options }
          : slot.kind === 'mapping'
            ? { left: parseMapping(slot.task.description ?? '').left }
            : {},
      );
    }
    return map;
  }, [slots]);

  const parsedFor = useCallback((slot: AnswerSlot) => parsedBySlot.get(slot.path) ?? {}, [parsedBySlot]);

  const answeredCount = useMemo(
    () =>
      slots.filter(slot => {
        const built = answerToPayload(slot.kind, answers[slot.path], parsedFor(slot));
        return Boolean(built.text || built.imageBase64);
      }).length,
    [slots, answers, parsedFor],
  );

  const setAnswer = useCallback(
    (path: string, next: TaskAnswerState) => {
      setAnswers(previous => ({ ...previous, [path]: next }));
      scheduleDraftWrite(path, next);
    },
    [scheduleDraftWrite],
  );

  // One stable `onChange` per slot, not a fresh closure per render — `AnswerSlotOverlay` below is wrapped in
  // `React.memo` specifically so a change to one slot's answer doesn't re-render every other slot (giant
  // inlined exam SVG included), but a new `onChange` reference on every render would defeat that memo check
  // for every slot regardless of whether its own answer changed. `slots` only changes with the exam layout,
  // so this map stays referentially stable across ordinary answer updates (typing, drawing, choosing).
  const onChangeForSlot = useMemo(() => {
    const handlers = new Map<string, (next: TaskAnswerState) => void>();
    for (const slot of slots) handlers.set(slot.path, next => setAnswer(slot.path, next));
    return handlers;
  }, [slots, setAnswer]);

  // Same reasoning as `onChangeForSlot`, keyed by material id instead of task path.
  const onChangeForMaterial = useMemo(() => {
    const handlers = new Map<string, (next: TaskAnswerState) => void>();
    for (const materialSlot of materialSlots) {
      handlers.set(materialSlot.materialId, next => setMaterialAnswer(materialSlot.materialId, next));
    }
    return handlers;
  }, [materialSlots, setMaterialAnswer]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitResult(null);

    const collected: StudentAnswerInput[] = [];
    for (const slot of slots) {
      const built = answerToPayload(slot.kind, answers[slot.path], parsedFor(slot));
      if (built.text || built.imageBase64) collected.push({ path: slot.path, ...built });
    }

    // Drawings made ON a material — where they end up (one task, several, or none yet) is resolved
    // server-side from the exam's own material↔task links, not guessed at here; the client only needs to
    // say WHICH material got which drawing. See `StudentMaterialAnswerInput`.
    const materials: StudentMaterialAnswerInput[] = [];
    for (const materialSlot of materialSlots) {
      const drawing = materialAnswers[materialSlot.materialId]?.drawing;
      if (drawing) materials.push({ materialId: materialSlot.materialId, imageBase64: drawing });
    }

    try {
      const result: any = await submitStudentExamMutation(
        examId,
        JSON.stringify({ answers: collected, materials }),
        studentName,
      )({
        success: 1,
        message: 1,
        submissionId: 1,
      });
      setSubmitResult({ success: Boolean(result?.success), message: String(result?.message ?? '') });
      if (result?.success) clearDraft();
    } catch {
      setSubmitResult({ success: false, message: t('StudentExam.errors.submitFailed') });
    } finally {
      setSubmitting(false);
    }
  };

  if (userLoading || loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-100 dark:bg-background">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 size={18} className="animate-spin" /> {t('StudentExam.loading')}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <CenteredNotice
        title={t('StudentExam.signInRequiredTitle')}
        body={t('StudentExam.signInRequiredBody')}
        action={
          <a href={authIntentUrl('login')} className="text-sm font-semibold text-brand-500 hover:underline">
            {t('StudentExam.signInAction')}
          </a>
        }
      />
    );
  }

  if (!payload?.ok || !payload.pageImageUrls?.length) {
    return (
      <CenteredNotice
        title={t('StudentExam.unavailableTitle')}
        body={t(payload?.message ?? 'StudentExam.errors.notFound')}
      />
    );
  }

  if (submitResult?.success) {
    return (
      <CenteredNotice
        title={t('StudentExam.submittedTitle')}
        body={t('StudentExam.submittedBody')}
        icon={<CheckCircle2 size={40} className="text-emerald-500" />}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-200/70 pb-24 dark:bg-slate-900">
      <div className="mx-auto w-full max-w-3xl px-4 py-6 xl:pr-0">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-50">
              {payload.title || t('StudentExam.examFallbackTitle')}
            </h1>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {[
                payload.subject,
                payload.class && t('StudentExam.classLabel', { class: payload.class }),
                payload.duration,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <input
            value={studentName}
            onChange={event => setStudentName(event.target.value)}
            placeholder={t('StudentExam.namePlaceholder')}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>

        {/*
          The printed pages are PNGs (`payload.pageImageUrls`), not the live exam SVG. The SVG display this
          replaced was a single root holding the WHOLE document — every page, every glyph, every ruled line
          as its own vector path, tens of thousands of DOM nodes — sitting in the same layout tree as every
          answer widget on the page. Chromium doesn't scope layout invalidation to "just the part that
          visibly changed": a Chrome trace earlier in this project showed `contain: strict` and
          `content-visibility: auto` around that SVG doing nothing to stop an unrelated layout read or write
          elsewhere on the page (a textarea growing a line, a drawing canvas measuring its box) from forcing
          a full recalculation of that entire subtree. That's what made "why is typing slow" and "why is
          drawing slow" the same bug wearing different clothes — removing each widget's own forced read
          fixed that widget's OWN synchronous cost, but the SVG was still there to be dirtied by everything
          else on the page. A raster background has no internal layout to invalidate: resizing a sibling
          costs the browser a cheap re-composite of an `<img>`, not a re-layout of a vector document.
          Each page gets `aspectRatio` set explicitly from `pageWidthPt`/`pageHeightPt` (not left to the
          image's own intrinsic size) so the on-screen height of every page is EXACTLY what `boxStyle`
          below assumes, regardless of the exact pixel dimensions the PDF→PNG rasterization happened to
          produce for that page.
        */}
        <div className="relative w-full bg-white shadow-xl">
          <div className="pointer-events-none flex flex-col">
            {Array.from({ length: pageCount }, (_, index) => (
              <img
                key={index}
                src={payload.pageImageUrls?.[index]}
                alt=""
                draggable={false}
                className="block w-full"
                style={{ aspectRatio: `${pageWidthPt} / ${pageHeightPt}` }}
              />
            ))}
          </div>

          <div className="absolute inset-0">
            {positioned.map(slot => (
              <AnswerSlotOverlay
                key={slot.path}
                slot={slot}
                answer={answers[slot.path]}
                onChange={onChangeForSlot.get(slot.path)!}
                pageWidthPt={pageWidthPt}
                pageHeightPt={pageHeightPt}
                pageCount={pageCount}
                pageImageUrls={payload.pageImageUrls ?? []}
              />
            ))}
            {materialSlots.map(materialSlot => (
              <MaterialSlotOverlay
                key={materialSlot.materialId}
                materialSlot={materialSlot}
                answer={materialAnswers[materialSlot.materialId]}
                onChange={onChangeForMaterial.get(materialSlot.materialId)!}
                pageWidthPt={pageWidthPt}
                pageHeightPt={pageHeightPt}
                pageCount={pageCount}
                pageImageUrls={payload.pageImageUrls ?? []}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 border-t border-slate-300 bg-white/95 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-4 px-4 py-3">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {t('StudentExam.answeredCount', { answered: answeredCount, total: slots.length })}
          </div>
          {submitResult && !submitResult.success && (
            <div className="flex items-center gap-1.5 text-sm text-red-600">
              <AlertCircle size={15} /> {t(submitResult.message)}
            </div>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || answeredCount === 0}
            className="ml-auto flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {t('StudentExam.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

function CenteredNotice({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex h-screen items-center justify-center bg-slate-100 p-4 dark:bg-background">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
        {icon && <div className="mb-3 flex justify-center">{icon}</div>}
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-50">{title}</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{body}</p>
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}
